/**
 * @file 规则配置 Schema 与规则快照（RuleSnapshot）契约。
 *
 * ## 权威来源
 *
 * 本文件是 `design/gdd/07-rule-config-schema.md` §2 的 **TypeScript 落地**，
 * 并受 `design/gdd/00-decisions.md` 的 D-01 ~ D-10 已决口径约束。
 * 两份文档冲突时以 `00-decisions.md` 为准。
 *
 * 字段名、枚举取值、默认值**必须逐字对齐 GDD**——这里任何一次「顺手改个更好听的名字」，
 * 都会让 quality-lead 按 GDD 写的单测对不上号。
 *
 * ## 快照机制
 *
 * 规则配置在**房间创建的那一刻**被固化成一份不可变快照，并生成 `ruleHash`。
 * 之后无论服务端配置怎么改、玩家怎么重连，同一房间用的都是同一份规则；
 * 客户端与服务端通过比对 `ruleHash` 即可发现「算分口径不一致」这类最难查的 bug。
 * 详见 `docs/architecture/adr/ADR-005-rule-config.md`。
 *
 * ## 本轮（M1-T2）范围
 *
 * 只有**类型定义 + 默认值常量**。校验函数 V-1 ~ V-16 的实现属于 M1-T3，
 * 此处仅以 {@link RuleValidationCode} 固定其错误码，避免实现时自创命名。
 */

/* ─────────────────────────── 版本 ─────────────────────────── */

/**
 * 配置结构版本号，对应 GDD §2 `RuleConfig.version`。
 * 校验 **V-1** 要求 `config.version === RULE_CONFIG_VERSION`。
 */
export const RULE_CONFIG_VERSION = 1 as const;

/**
 * core 侧 Schema 血缘标签。与 {@link RULE_CONFIG_VERSION} 的区别：
 * 前者是**对外契约版本**（写进存档、参与迁移路由），
 * 后者是**内部实现版本**（新增可选字段、修默认值时递增，不破坏兼容性）。
 * 握手时两端都会比对它，不一致只告警不拒绝。
 */
export const RULE_SCHEMA_VERSION = '1.0.0' as const;

/* ─────────────────────────── 番型 ─────────────────────────── */

/**
 * 番型枚举 key，与 `design/gdd/04-scoring.md` §2 一一对应。
 *
 * 分组：
 * - 基础番型：`DUI_DUI_HU` / `QING_YI_SE` / `QI_DUI` / `GEN` / `JIN_GOU_DIAO`
 * - 情境番型：`GANG_SHANG_HUA` / `GANG_SHANG_PAO` / `QIANG_GANG_HU` / `HAI_DI_LAO_YUE`
 * - 可选番型（M1 实现但默认关闭）：`TIAN_HU` / `DI_HU` / `HAI_DI_PAO` / `JIANG_DUI`
 *
 * ⚠ 复合番型（清对 / 龙七对 / 清七对 / 双龙 / 清龙 / 十八罗汉）**不在此列**——
 * 按 D-05 加法番数模型，它们由基础番型相加自然得出，不设独立枚举、不做特判。
 */
export type FanKey =
  | 'DUI_DUI_HU'
  | 'QING_YI_SE'
  | 'QI_DUI'
  | 'GEN'
  | 'JIN_GOU_DIAO'
  | 'GANG_SHANG_HUA'
  | 'GANG_SHANG_PAO'
  | 'QIANG_GANG_HU'
  | 'HAI_DI_LAO_YUE'
  | 'TIAN_HU'
  | 'DI_HU'
  | 'HAI_DI_PAO'
  | 'JIANG_DUI';

/**
 * 番型 key 的运行时清单。校验 V-5（遍历所有 `fan[k].fan`）与
 * 规范序列化（按此顺序输出，避免对象键序影响 `ruleHash`）都要用到。
 */
export const FAN_KEYS = [
  'DUI_DUI_HU',
  'QING_YI_SE',
  'QI_DUI',
  'GEN',
  'JIN_GOU_DIAO',
  'GANG_SHANG_HUA',
  'GANG_SHANG_PAO',
  'QIANG_GANG_HU',
  'HAI_DI_LAO_YUE',
  'TIAN_HU',
  'DI_HU',
  'HAI_DI_PAO',
  'JIANG_DUI',
] as const satisfies readonly FanKey[];

/** 单个番型的配置。 */
export interface FanRule {
  /** 是否启用该番型。关闭后该番型不参与计番。 */
  readonly enabled: boolean;
  /** 番数。总倍率 = `2 ^ min(Σfan, fanCap)`。校验 V-5：整数且 ∈ [0, 20]。 */
  readonly fan: number;
}

/** 番型表。键为 {@link FanKey}，缺任何一项都不合法。 */
export type FanTable = Readonly<Record<FanKey, FanRule>>;

/* ─────────────────────────── §2.1 计分 ─────────────────────────── */

/** 计分配置。 */
export interface ScoringConfig {
  /** 底分。`score = baseScore × 2^min(Σfan, fanCap)`。校验 V-2：整数且 ∈ [1, 100]。 */
  readonly baseScore: number;

  /**
   * 封顶番数。3 = ×8，4 = ×16，5 = ×32，99 = 不封顶。
   *
   * ★ **D-01：M1 锁定为 4**（成都主流 ×16 封顶）。
   * 校验 V-3 仍放行 [0, 99] 以便 M2/M3 的「休闲 / 刺激」预设使用，
   * 但 M1 引擎必须以 4 结算；改动此默认值等于改动全部计分算例。
   */
  readonly fanCap: number;

  /** 是否允许玩家分数为负。 */
  readonly allowNegativeScore: boolean;

  /**
   * 单局个人输赢上限（0 = 不限制）。
   *
   * ⚠ 启用会破坏「Σ finalScore === 0」守恒，需按比例再平衡。
   * 校验 V-15 在 M1 强制其为 0。
   */
  readonly perGameLossCap: number;
}

/* ─────────────────────────── §2.2 杠分 ─────────────────────────── */

/**
 * 明杠支付方式。
 * - `DEALER_ONLY`：仅放杠者支付（成都主流）
 * - `ALL_OTHERS`：其余每家各付一份（M1 未实现，V-16 锁定）
 */
export type MingGangPayer = 'DEALER_ONLY' | 'ALL_OTHERS';

/** 杠分配置。所有取值校验 V-4：整数且 ∈ [0, 50]。 */
export interface GangScoreConfig {
  /** 明杠（直杠）分数。 */
  readonly mingGang: number;
  /** 暗杠分数。 */
  readonly anGang: number;
  /** 巴杠（补杠）分数。 */
  readonly baGang: number;
  /** 明杠的支付方式。M1 锁定 `DEALER_ONLY`（V-16）。 */
  readonly mingGangPayer: MingGangPayer;
  /** 杠分是否受 `fanCap` 封顶影响。成都主流：杠分不封顶。 */
  readonly applyFanCapToGang: boolean;
}

/* ─────────────────────────── §2.4 杠规则 ─────────────────────────── */

/** 杠的流程规则。 */
export interface GangConfig {
  /** 杠后从牌尾补摸（`true`，成都主流）还是从牌头（`false`）。 */
  readonly drawFromTail: boolean;
  /** 巴杠是否必须使用本回合刚摸到的那张牌。`false` = 宽松。 */
  readonly baGangRequiresJustDrawn: boolean;
  /** 杠上炮是否由杠牌者「包三家」（承担全部赔付）。M1 未实现。 */
  readonly gangPaoBaoPei: boolean;
  /**
   * 牌墙剩余不足时是否禁止杠。
   *
   * ⚠ 必须为 `true`，否则会出现「杠了却无牌可补摸」的死锁。
   * 保留为配置项仅为显式化该约束；校验 V-9 强制 `true`。
   */
  readonly forbidGangWhenWallEmpty: boolean;
}

/* ─────────────────────────── §2.5 抢杠胡 ─────────────────────────── */

/** 抢杠胡配置。 */
export interface QiangGangConfig {
  /** 是否开启抢杠胡。 */
  readonly enabled: boolean;
  /** 巴杠可否被抢。成都主流为 `true`（逻辑等同「打出第 4 张」）。 */
  readonly allowBaGang: boolean;
  /**
   * 暗杠可否被抢。
   *
   * ★ **D-07：M1 锁定 `false`**——暗杠是完全私有牌，不视为打出。
   * 校验 V-11 强制 `false`。
   */
  readonly allowAnGang: boolean;
}

/* ─────────────────────────── §2.6 胡牌 ─────────────────────────── */

/**
 * 地胡判定口径。
 * - `ZIMO`：闲家第一次摸牌自摸
 * - `DIANPAO_FIRST`：闲家胡庄家打出的第一张牌
 */
export type DiHuMode = 'ZIMO' | 'DIANPAO_FIRST';

/** 胡牌规则。 */
export interface HuConfig {
  /** 是否启用「过水不胡」（振听）。川麻传统无此规则。M1 未实现。 */
  readonly enableGuoShuiBuHu: boolean;
  /** 地胡判定口径。 */
  readonly diHuMode: DiHuMode;
  /**
   * 多种拆解时的取番策略。`MAX` = 取最大番。
   * M1 仅支持 `MAX`（V-10）。
   */
  readonly decompositionPolicy: 'MAX';
}

/* ─────────────────────────── §2.7 血战到底 ─────────────────────────── */

/** 血战到底流程配置。 */
export interface XuezhanConfig {
  /**
   * 是否允许一炮多响。
   *
   * ★ **D-03：M1 锁定 `true`**。配套规格见 `design/gdd/05-xuezhan-flow.md` §4.3
   * 「虚拟胡牌张守恒」——点炮牌实体只归 `ordered[0]`，其余胡家记虚拟牌，
   * `assertStateInvariants` 的张数守恒检查必须排除虚拟牌。
   */
  readonly multipleHu: boolean;
  /** 已胡玩家是否仍需支付后续玩家的自摸分。M1 锁定 `false`（V-13）。 */
  readonly wonPlayerPaysZimo: boolean;
  /** 已胡玩家是否仍需支付后续杠分。M1 锁定 `false`（V-13）。 */
  readonly wonPlayerPaysGang: boolean;
  /**
   * 已胡玩家胡牌时是否亮牌公示。
   *
   * ⚠ 这是**信息隔离的配置开关**：为 `true` 时该座位手牌进入公开事件，
   * 投影层必须据此放行，不能一刀切地过滤（见 `view.ts`）。
   */
  readonly revealHandOnWin: boolean;
}

/* ─────────────────────────── §2.8 查大叫 / 呼叫转移 ─────────────────────────── */

/**
 * 呼叫转移口径。
 * - `REFUND_TO_PAYER`：原路退还给当初的支付方
 * - `TRANSFER_TO_TING`：转移给听牌玩家（M1 未实现）
 */
export type GangRefundMode = 'REFUND_TO_PAYER' | 'TRANSFER_TO_TING';

/** 查大叫与呼叫转移配置。 */
export interface ChaDaJiaoConfig {
  /** 是否开启查大叫。 */
  readonly enabled: boolean;
  /**
   * 是否要求「有效叫」——听的牌必须仍有剩余才算听牌。
   * `false` = 听绝张也算有叫（成都主流，D-08）。
   */
  readonly requireLiveWait: boolean;
  /**
   * `THREE_WON` 终局时是否对最后一家查叫。
   *
   * ★ **D-04：M1 锁定 `false`**——三家已胡时牌未打完，末家是被「打完了」
   * 而非自己不努力，不罚。
   */
  readonly applyOnThreeWon: boolean;
  /** 是否开启呼叫转移（退杠分）。 */
  readonly gangRefundEnabled: boolean;
  /**
   * 呼叫转移口径。
   *
   * ★ **D-09：M1 锁定 `REFUND_TO_PAYER`**——未听牌者所收杠分逐笔原路退还
   * 当初支付方，不做链式转移。校验 V-12 强制。
   */
  readonly gangRefundMode: GangRefundMode;
  /**
   * ★ **D-02：reserved / 不实现**。花猪惩罚番数，恒为 0。
   *
   * `design/gdd/02-core-rules.md` §1.5 强制实施 QC-1（有缺门牌时只能打缺门牌），
   * 玩家在数学上永远无法在终局残留缺门牌 → **花猪在本实现中不可能出现**。
   * M1 引擎不得据此字段产生任何分数流向；校验 V-14 强制其为 0。
   *
   * 解锁条件：若未来放宽强制打缺，需同步解除本字段与 V-14 的锁定。
   */
  readonly huaZhuPenaltyFan: number;
}

/* ─────────────────────────── §2.9 牌墙 ─────────────────────────── */

/**
 * 洗牌随机源。
 * - `CSPRNG`：线上默认。⚠ core 是平台无关纯函数包，**不自行取随机**——
 *   宿主（Worker / 浏览器）用平台 CSPRNG 生成 32 位种子后注入 `createGame()`，
 *   core 内部一律使用注入种子驱动的确定性 PRNG（见 `types.ts` 的 `RngState`）。
 * - `SEEDED`：固定种子，仅用于单测与回放。
 */
export type ShuffleSource = 'CSPRNG' | 'SEEDED';

/** 牌墙配置。 */
export interface WallConfig {
  /** 留底牌张数（不可摸）。校验 V-7：∈ [0, 20]。 */
  readonly reservedTiles: number;
  /** 洗牌随机源。 */
  readonly shuffleSource: ShuffleSource;
  /** `shuffleSource === 'SEEDED'` 时使用的固定种子。 */
  readonly seed?: number;
}

/* ─────────────────────────── §2.10 超时 ─────────────────────────── */

/**
 * 各阶段超时时长（毫秒）。
 *
 * core **不读时钟**：到点由宿主注入 `TimeoutAction`，引擎只负责执行默认动作。
 * 校验 V-6：所有 `*Ms` ∈ [1000, 300000]。
 */
export interface TimeoutConfig {
  /** 定缺阶段超时。超时走 `autoPickQue`。 */
  readonly decideQueMs: number;
  /** 出牌超时。超时自动打出手牌最右一张（有缺门牌时优先打缺门牌）。 */
  readonly discardMs: number;
  /** 碰 / 杠 / 胡响应窗口超时。超时视为 PASS。 */
  readonly responseMs: number;
  /** 抢杠胡响应窗口超时。 */
  readonly qiangGangMs: number;
  /**
   * 断线重连宽限期，超过则由 AI 托管。
   *
   * ⚠ 该值直接决定 Durable Object 的驻留时长与免费额度消耗，
   * 见 `docs/deployment/zero-cost-plan.md` §5。调大它等于按比例调高账单风险。
   */
  readonly reconnectGraceMs: number;
  /** 连续超时几次后转为 AI 托管。 */
  readonly autoPlayAfterTimeouts: number;
}

/* ─────────────────────────── §2.11 AI（M2 生效） ─────────────────────────── */

/** AI 难度档位。 */
export type AIDifficulty = 'EASY' | 'NORMAL' | 'HARD';

/** 估值权重覆盖，用于线上调优；未提供时用档位预设。 */
export interface AIWeightOverrides {
  readonly wEfficiency?: number;
  readonly wSafety?: number;
  readonly wYaku?: number;
  readonly wFlexibility?: number;
  readonly epsilon?: number;
}

/** AI 配置。M2 生效，M1 仅占位。 */
export interface AIConfig {
  readonly difficulty: AIDifficulty;
  /** 是否允许 AI 放弃小番胡牌去搏大牌。 */
  readonly allowSkipSmallHu: boolean;
  /** 拟人化决策延迟范围，`[min, max]` 毫秒。由宿主负责实际等待，core 不睡眠。 */
  readonly thinkDelayMs: readonly [number, number];
  /** 估值权重覆盖。 */
  readonly weights?: AIWeightOverrides;
}

/* ─────────────────────────── §2.12 变体扩展位 ─────────────────────────── */

/** 癞子牌确定方式。 */
export type JokerPickMode = 'FIXED' | 'FLIP' | 'DICE';
/** 喜牌确定方式。 */
export type XiPaiPickMode = 'DICE' | 'LAST_TILE' | 'FIXED';
/** 换三张交换方向。 */
export type HuanSanZhangMode = 'RANDOM' | 'CLOCKWISE' | 'COUNTER_CLOCKWISE' | 'ACROSS';

/** 【幺鸡癞子】一条作万能牌。⚠ M1 不实现。 */
export interface JokerVariant {
  readonly enabled: boolean;
  /** 癞子牌种（`TileKind`），默认一条。 */
  readonly jokerTileKey: number;
  /** 癞子能否作将牌。 */
  readonly canBeEye: boolean;
  /** 使用癞子胡牌的番数惩罚（负数为减番）。 */
  readonly fanPenalty: number;
  /** 天生癞子（自摸到癞子本身）另计番数。 */
  readonly naturalJokerFan: number;
  /** 癞子确定方式。 */
  readonly pickMode: JokerPickMode;
}

/** 【喜牌】开局翻出一张加番指示牌。⚠ M1 不实现。 */
export interface XiPaiVariant {
  readonly enabled: boolean;
  /** 每张喜牌加的番数。 */
  readonly fanPerTile: number;
  /** 喜牌确定方式。 */
  readonly pickMode: XiPaiPickMode;
  /** `pickMode === 'FIXED'` 时使用。 */
  readonly fixedTileKey?: number;
}

/** 【坎数番】每个暗刻加番。⚠ M1 不实现。 */
export interface KanShuFanVariant {
  readonly enabled: boolean;
  /** 每个暗刻加的番数。 */
  readonly fanPerKan: number;
  /** 暗杠是否计入坎数。 */
  readonly countAnGangAsKan: boolean;
  /** 是否与「根」重复计算。 */
  readonly stackWithGen: boolean;
}

/** 【换三张】开局定缺前交换 3 张同花色牌。⚠ M1 不实现。 */
export interface HuanSanZhangVariant {
  readonly enabled: boolean;
  readonly mode: HuanSanZhangMode;
}

/** 【血流成河】胡牌后不退出。⚠ M1 不实现（会重写整套流程状态机）。 */
export interface XueLiuChengHeVariant {
  readonly enabled: boolean;
  /** 单人单局最大胡牌次数（0 = 不限）。 */
  readonly maxWinsPerPlayer: number;
}

/** 【推倒胡 / 无定缺模式】关闭定缺强制，用于新手教学。⚠ M1 不实现。 */
export interface NoQueModeVariant {
  readonly enabled: boolean;
}

/**
 * 变体扩展位。**M1 全部不实现**，仅预留字段以避免日后破坏性改 Schema。
 *
 * ⚠ GDD §1 原则 P5：M1 引擎遇到任一 `enabled: true` 必须**明确抛错**
 * （`RuleConfigError` + `UNSUPPORTED_RULE` + 具体变体名），而非静默忽略。
 * 静默忽略会让用户以为「开了没效果 = bug」，比直接报错难查一个数量级。
 * 校验 V-8 负责这条。
 */
export interface VariantConfig {
  readonly joker: JokerVariant;
  readonly xiPai: XiPaiVariant;
  readonly kanShuFan: KanShuFanVariant;
  readonly huanSanZhang: HuanSanZhangVariant;
  readonly xueLiuChengHe: XueLiuChengHeVariant;
  readonly noQueMode: NoQueModeVariant;
}

/* ─────────────────────────── 顶层配置 ─────────────────────────── */

/**
 * 四川麻将（血战到底）规则配置。
 *
 * 这是**唯一**允许影响算分与流程的外部输入。
 * GDD §1 原则 P1：引擎内部禁止出现任何硬编码的番数、底分、杠分、超时值——
 * 代码里出现裸常量 `* 2` / `+ 1` 参与计分即为违规（验收项 AC-07-1）。
 */
export interface RuleConfig {
  /** 结构版本号，固定为 {@link RULE_CONFIG_VERSION}。校验 V-1。 */
  readonly version: typeof RULE_CONFIG_VERSION;
  /** 预设名。仅用于 UI 展示与埋点，**不影响任何逻辑**。 */
  readonly presetName?: string;

  readonly scoring: ScoringConfig;
  readonly gangScore: GangScoreConfig;
  readonly fan: FanTable;
  readonly gang: GangConfig;
  readonly qiangGang: QiangGangConfig;
  readonly hu: HuConfig;
  readonly xuezhan: XuezhanConfig;
  readonly chaDaJiao: ChaDaJiaoConfig;
  readonly wall: WallConfig;
  readonly timeout: TimeoutConfig;
  readonly ai: AIConfig;
  /** 变体扩展位，M1 全部关闭。 */
  readonly variant: VariantConfig;
}

/**
 * @deprecated 保留给早期引用方的别名，新代码一律用 {@link RuleConfig}。
 * 下个里程碑清理。
 */
export type SichuanRuleConfig = RuleConfig;

/** 递归可选，用于「只传差异项」的建房请求。 */
export type DeepPartial<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { readonly [K in keyof T]?: DeepPartial<T[K]> }
    : T;

/**
 * 建房时提交的规则差异项。
 * 服务端以 {@link CHENGDU_STANDARD} 为基线合并，再走快照管线。
 */
export type RuleConfigPatch = DeepPartial<RuleConfig>;

/* ─────────────────────────── 默认预设 ─────────────────────────── */

/**
 * 成都标准预设——`design/gdd/07-rule-config-schema.md` §3 的逐字落地。
 *
 * ⚠ 这是**数据**不是实现：它只声明默认取值，不做深拷贝、不做校验、不冻结。
 * 拿去用之前必须过 `createRuleSnapshot()`（M1-T3 实现），否则调用方可能改到共享引用。
 *
 * ⚠ 修改本常量任一数值，等于修改 `design/gdd/04-scoring.md` 的全部算例。
 * 改动前先改 GDD，并同步 `tests/rule-decisions.test.ts`。
 */
export const CHENGDU_STANDARD: RuleConfig = {
  version: RULE_CONFIG_VERSION,
  presetName: '成都标准',

  scoring: {
    baseScore: 1,
    fanCap: 4, // ★ D-01：M1 锁定 ×16 封顶
    allowNegativeScore: true,
    perGameLossCap: 0, // V-15 锁定
  },

  gangScore: {
    mingGang: 1,
    anGang: 2,
    baGang: 1,
    mingGangPayer: 'DEALER_ONLY', // V-16 锁定
    applyFanCapToGang: false,
  },

  fan: {
    // 基础番型（M1 全部启用）
    DUI_DUI_HU: { enabled: true, fan: 1 }, // 对对胡 ×2
    QING_YI_SE: { enabled: true, fan: 2 }, // 清一色 ×4
    QI_DUI: { enabled: true, fan: 2 }, // 七对   ×4
    GEN: { enabled: true, fan: 1 }, // 每根   ×2
    JIN_GOU_DIAO: { enabled: true, fan: 1 }, // 金钩钓 ×2
    // 情境番型（M1 全部启用）
    GANG_SHANG_HUA: { enabled: true, fan: 1 },
    GANG_SHANG_PAO: { enabled: true, fan: 1 },
    QIANG_GANG_HU: { enabled: true, fan: 1 },
    HAI_DI_LAO_YUE: { enabled: true, fan: 1 },
    // 可选番型（M1 实现但默认关闭）
    TIAN_HU: { enabled: false, fan: 4 }, // ★ D-10
    DI_HU: { enabled: false, fan: 3 }, // ★ D-10
    HAI_DI_PAO: { enabled: false, fan: 1 },
    JIANG_DUI: { enabled: false, fan: 2 },
  },

  gang: {
    drawFromTail: true,
    baGangRequiresJustDrawn: false,
    gangPaoBaoPei: false,
    forbidGangWhenWallEmpty: true, // V-9 锁定：防「杠了无牌可补」死锁
  },

  qiangGang: {
    enabled: true,
    allowBaGang: true,
    allowAnGang: false, // ★ D-07 / V-11 锁定
  },

  hu: {
    enableGuoShuiBuHu: false,
    diHuMode: 'ZIMO',
    decompositionPolicy: 'MAX', // V-10 锁定
  },

  xuezhan: {
    multipleHu: true, // ★ D-03 锁定开启
    wonPlayerPaysZimo: false, // V-13 锁定
    wonPlayerPaysGang: false, // V-13 锁定
    revealHandOnWin: true,
  },

  chaDaJiao: {
    enabled: true,
    requireLiveWait: false, // D-08：听绝张也算有叫
    applyOnThreeWon: false, // ★ D-04 锁定
    gangRefundEnabled: true,
    gangRefundMode: 'REFUND_TO_PAYER', // ★ D-09 / V-12 锁定
    huaZhuPenaltyFan: 0, // ★ D-02 reserved / V-14 锁定
  },

  wall: {
    reservedTiles: 0,
    shuffleSource: 'CSPRNG',
  },

  timeout: {
    decideQueMs: 15000,
    discardMs: 15000,
    responseMs: 8000,
    qiangGangMs: 8000,
    reconnectGraceMs: 60000,
    autoPlayAfterTimeouts: 3,
  },

  ai: {
    difficulty: 'NORMAL',
    allowSkipSmallHu: false,
    thinkDelayMs: [500, 1200],
  },

  variant: {
    joker: {
      enabled: false,
      jokerTileKey: 9,
      canBeEye: false,
      fanPenalty: 0,
      naturalJokerFan: 0,
      pickMode: 'FIXED',
    },
    xiPai: { enabled: false, fanPerTile: 1, pickMode: 'DICE' },
    kanShuFan: { enabled: false, fanPerKan: 1, countAnGangAsKan: true, stackWithGen: false },
    huanSanZhang: { enabled: false, mode: 'RANDOM' },
    xueLiuChengHe: { enabled: false, maxWinsPerPlayer: 0 },
    noQueMode: { enabled: false },
  },
};

/* ─────────────────────────── M1 护栏 ─────────────────────────── */

/**
 * M1 的「未实现护栏」取值表，对应 GDD §5 的 V-8 ~ V-16。
 *
 * 这些字段在 Schema 里存在、在实现里**尚未支持**。护栏的作用是防止
 * 配置层跑在实现层前面——用户把开关打开、引擎静默按旧逻辑算，
 * 结算错了还查不出来。每实现一项，就从这里删掉一条。
 *
 * 校验函数 `normalizeRuleConfig()` 在 M1-T3 实现，本轮只固化期望值。
 */
export const M1_LOCKED_VALUES = {
  /** V-9：防死锁 */
  'gang.forbidGangWhenWallEmpty': true,
  /** V-10：仅支持取最大番拆解 */
  'hu.decompositionPolicy': 'MAX',
  /** V-11 / D-07：不实现抢暗杠 */
  'qiangGang.allowAnGang': false,
  /** V-12 / D-09：仅实现原路退还 */
  'chaDaJiao.gangRefundMode': 'REFUND_TO_PAYER',
  /** V-13：已胡玩家不再支付自摸分 */
  'xuezhan.wonPlayerPaysZimo': false,
  /** V-13：已胡玩家不再支付杠分 */
  'xuezhan.wonPlayerPaysGang': false,
  /** V-14 / D-02：花猪 reserved，恒为 0 */
  'chaDaJiao.huaZhuPenaltyFan': 0,
  /** V-15：不实现单局输赢上限（会破坏分数守恒） */
  'scoring.perGameLossCap': 0,
  /** V-16：明杠仅放杠者支付 */
  'gangScore.mingGangPayer': 'DEALER_ONLY',
} as const;

/**
 * M1 结算强制使用的封顶番数（D-01）。
 *
 * 注意与 {@link M1_LOCKED_VALUES} 的区别：`fanCap` **不是**护栏字段——
 * V-3 仍放行 [0, 99] 供 M2/M3 的其他预设使用，M1 引擎只是把结算口径固定在 4。
 */
export const M1_ENFORCED_FAN_CAP = 4 as const;

/**
 * 配置校验错误码，与 GDD §5 的 V-1 ~ V-16 一一对应。
 * 固定在此是为了防止 M1-T3 实现时自创命名，导致前端错误提示对不上。
 */
export type RuleValidationCode =
  | 'UNSUPPORTED_CONFIG_VERSION' // V-1
  | 'INVALID_BASE_SCORE' // V-2
  | 'INVALID_FAN_CAP' // V-3
  | 'INVALID_GANG_SCORE' // V-4
  | 'INVALID_FAN_VALUE' // V-5
  | 'INVALID_TIMEOUT' // V-6
  | 'INVALID_RESERVED_TILES' // V-7
  | 'UNSAFE_CONFIG' // V-9
  | 'UNSUPPORTED_RULE'; // V-8, V-10 ~ V-16

/* ─────────────────────────── 规则快照 ─────────────────────────── */

/**
 * 房间级不可变规则快照。
 *
 * 生成管线（`createRuleSnapshot()`）严格按序执行五步：
 * 1. **深拷贝**——切断与调用方对象的引用，防止外部后续改动串味（GDD 原则 P2）；
 * 2. **规范化**（`normalizeRuleConfig`）——以 {@link CHENGDU_STANDARD} 补默认值、
 *    忽略未知字段（P4 向前兼容）、执行 V-1 ~ V-16 校验；
 * 3. **规范序列化**（`canonicalizeRuleConfig`）——键按字典序递归排序、
 *    数字统一为最短往返表示，保证「语义相同的配置 → 逐字节相同的字符串」；
 * 4. **哈希**（`hashRuleConfig`）——对规范 JSON 求 64 位 FNV-1a，得 `ruleHash`；
 *    非加密哈希即可，用途是**一致性检测**而非防篡改（防篡改由服务端权威保证）；
 * 5. **冻结**——递归 `Object.freeze`，让越权修改在开发期即刻抛错。
 */
export interface RuleSnapshot {
  /** 深冻结后的规则配置。 */
  readonly config: RuleConfig;
  /** 规范 JSON 的 64 位 FNV-1a 哈希，16 位小写十六进制。 */
  readonly ruleHash: string;
  /** 规范序列化结果。保留它便于排查「哈希不一致」时的具体差异。 */
  readonly canonicalJson: string;
  /** 快照生成时的 core Schema 版本。 */
  readonly schemaVersion: string;
}

/** `normalizeRuleConfig()` 的问题描述。 */
export interface RuleConfigIssue {
  /** 出问题的字段路径，如 `'scoring.fanCap'`。 */
  readonly path: string;
  /** 对应的校验错误码。 */
  readonly code: RuleValidationCode;
  readonly message: string;
  readonly severity: 'error' | 'warning';
}
